import React from 'react'
import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import { useSearchParams } from 'react-router-dom'
import ROUTES from '../../../../consts/paths'
import { type TableCell } from '../../../../models/CompletionRate'
import { grey, shade, yellow, green } from '../../../../colors'
import CustomTooltip from '../../../../components/CustomTooltip'
import { formatDateByUserTimeZone } from '../../../../helpers/formatDateByUserTimeZone'

const CROSS_LISTED_TOOLTIP =
  'This Course Section is cross-listed. The survey score here reflects only this Course Section, while the section-level report may show the aggregate of all cross-listed sections — so the scores can differ.'
const LAST_UPDATED_DATE_FORMAT = "MMM d, yyyy 'at' h:mm a"

/**
 * Shuffle (cross-list) icon shown beside a cross-listed Course Section's id on
 * the instructor dashboard. Rendered only when the row's `isCrosslistedSection`
 * flag is true. Stops click propagation so the icon never triggers row navigation.
 */
export const renderCrossListedInfo = (): JSX.Element => (
  <CustomTooltip title={CROSS_LISTED_TOOLTIP} placement="top">
    <ShuffleIcon
      aria-label="This Course Section is cross-listed"
      onClick={(e) => { e.stopPropagation() }}
      sx={{ color: grey[600], fontSize: 16, cursor: 'pointer', display: 'block' }}
    />
  </CustomTooltip>
)

interface Instructor {
  id: string
  title: string
  isInScope?: boolean
}

/**
 * Render a survey score as a colored Chip:
 * - greater than parent → green
 * - less than parent    → yellow
 * - equal to parent     → gray
 */
export const renderSurveyScore = (
  row: TableCell,
  parentSurveyScore: number | null | undefined
): JSX.Element => {
  const theme = useTheme()
  const score = row.surveyScore
  if (score == null || score === 0.0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: `0 ${theme.spacing(1)}`,
          width: '50px'

        }}
      >
        <Typography sx={{ color: grey[850] }}>--</Typography>
      </Box>
    )
  }

  const parent = parentSurveyScore ?? 0

  let backgroundColor: string
  let borderColor: string
  let textColor: string

  const difference = score - parent

  switch (true) {
    case difference > 0:
      // bigger than → green
      backgroundColor = green[0]
      borderColor = green[200]
      textColor = green[200]
      break
    case difference < 0:
      // smaller than → yellow
      backgroundColor = yellow[100]
      borderColor = yellow[400]
      textColor = yellow[500]
      break
    default:
      // exactly equal → gray
      backgroundColor = shade[300]
      borderColor = shade[500]
      textColor = grey[500]
      break
  }

  return (
    <Chip
      label={score.toFixed(2)}
      variant="filled"
      size="small"
      sx={{
        fontWeight: 'bold',
        border: 'none',
        backgroundColor,
        color: textColor,
        borderRadius: theme.spacing(1.5),
        padding: `0 ${theme.spacing(1)}`,
        height: theme.spacing(6),
        lineHeight: '1'
      }}
    />
  )
}

/**
 * CR-A6-RR: per-section "is the report viewable to the caller's active role
 * right now?" chip. The BE precomputes the boolean against `can_view_report`'s
 * predicate (standard release, plus role-gated admin/instructor/system early-
 * access dates) and stamps it on every A6 Section-children row. We render the
 * value as a chip; absent values fall back to "--" so older payloads or
 * non-A6 reuses don't show a misleading chip.
 */
export const renderIsReportReleased = (row: TableCell): JSX.Element => {
  const value = row.isReportReleased
  if (typeof value !== 'boolean') {
    return <Typography sx={{ color: grey[850] }}>--</Typography>
  }
  const isReleased = value
  const label = isReleased ? 'Released' : 'Pending'
  const tooltip = isReleased
    ? 'The report is viewable now under your active role.'
    : 'The report is not yet viewable under your active role.'
  const backgroundColor = isReleased ? green[0] : yellow[100]
  const borderColor = isReleased ? green[200] : yellow[400]
  const textColor = isReleased ? green[200] : yellow[500]
  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Chip
        label={label}
        size="small"
        variant="filled"
        aria-label={isReleased ? 'Report released' : 'Report not released yet'}
        sx={{
          fontWeight: 'bold',
          border: `1px solid ${borderColor}`,
          backgroundColor,
          color: textColor,
          borderRadius: 1.5,
          height: 24,
          lineHeight: 1
        }}
      />
    </Tooltip>
  )
}

/**
 * CR-271 Behavior Change #9: "Last updated" timestamp for a Section row's underlying
 * report data. The column itself is Account-Owner-only — SectionTable filters
 * `modifiedOn` out of the Backend-provided key list for every other role, so this
 * renderer only ever runs for Account Owner in practice. Formats in the viewer's
 * local/system timezone to match other report date surfaces.
 */
export const renderModifiedOn = (row: TableCell): JSX.Element => {
  const value = row.modifiedOn
  if (typeof value !== 'string') {
    return <Typography sx={{ color: grey[850] }}>--</Typography>
  }
  const lastUpdatedAt = formatDateByUserTimeZone(value, LAST_UPDATED_DATE_FORMAT)
  return <Typography sx={{ color: grey[850] }}>Last updated: {lastUpdatedAt || '--'}</Typography>
}

export const renderInstructorDetails = (row: TableCell): JSX.Element => {
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('projectId')

  const rawInstructors = row.instructorDetails ?? []
  if (rawInstructors.length === 0) {
    return <Typography>--</Typography>
  }
  // CE-39 follow-up: render accessible instructors first, restricted ones last.
  // Stable partition preserves the BE-provided order inside each group.
  const instructors = [
    ...rawInstructors.filter(i => i?.isInScope !== false),
    ...rawInstructors.filter(i => i?.isInScope === false)
  ]

  const renderEntry = (inst: Instructor): JSX.Element => {
    // is_in_scope is emitted only when the caller has a Faculty Attribute scope
    // (CE-39 follow-up). Absent or true → render as a link. Explicit false → muted text.
    if (inst.isInScope === false) {
      return (
        <Typography
          key={inst.id}
          sx={{
            color: grey[500],
            mb: 0.5,
            lineHeight: 1.5
          }}
        >
          {inst.title}
        </Typography>
      )
    }
    const url = `${ROUTES.INSTRUCTOR_SURVEYS}/${inst.id}?termId=${termId ?? ''}&projectId=${seriesId ?? ''}`
    return (
      <Box
        key={inst.id}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          window.open(url, '_blank')
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          textDecoration: 'underline',
          color: 'primary.main',
          cursor: 'pointer',
          mb: 0.5,
          lineHeight: 1.5
        }}
      >
        <Typography>{inst.title}</Typography>
        <ArrowOutwardIcon sx={{ fontSize: 16 }} />
      </Box>
    )
  }

  const [first, ...rest] = instructors
  const theme = useTheme()

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {renderEntry(first)}
      {rest.length > 0 && (
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: shade[200],
                border: `1px solid ${grey[700]}`,
                p: theme.spacing(2),
                maxWidth: 300
              }
            }
          }}
          title={<Box>{rest.map(renderEntry)}</Box>}
          placement="top"
          arrow
        >
          <Typography sx={{ cursor: 'pointer', color: 'primary.main' }}>
            +{rest.length}
          </Typography>
        </Tooltip>
      )}
    </Box>
  )
}
